import React from "react";
import { useDispatch } from "react-redux";
import { Modal } from "antd";
import { useIntl } from "react-intl";
import { updateRecordComment, createRecordComment } from "../../gaps/gapsSlice";
import { useSelector } from "react-redux";
import AddEntityForm from "../../../components/templates/AddEntityForm";
import { TextAreaItem } from "../../../components/forms/FormItems";
import { SettingsDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import {
  getPatientAbsences,
  getPatientHospitalizations,
  getPatient,
  getEmployeeAbsences,
} from "../../../api/server";
import { useParams, useLocation } from "react-router-dom";
import { isAllowedFeature } from "../../organization/organizationSlice";

const EditCommentModal = (props) => {
  const { record, hide, onReloadGaps, type } = props;
  let location = useLocation();
  let { branchId } = useParams();

  const intl = useIntl();
  const dispatch = useDispatch();
  const createLoading = useSelector(
    (state) => state.pendingActions["gaps/create_gap_comment"]
  );
  const updateLoading = useSelector(
    (state) => state.pendingActions["gaps/update_gap_comment"]
  );
  const scopePrefix = useSelector((state) => state.user.scopePrefix);

  const hasManagedGapReasonsFeature = isAllowedFeature("managed_gap_reasons");
  const onFinish = async (values) => {
    const { comment, managed_gap_reason_id } = values;
    const { order_id, month, year, order_gap_reason_comment_id } = record;
    const params = {
      order_id,
      comment,
      month,
      year,
      order_gap_reason_comment_id,
      scopePrefix,
    };

    if (hasManagedGapReasonsFeature) {
      params.managed_gap_reason_id = managed_gap_reason_id;
    }
    const action =
      type === "edit"
        ? await dispatch(updateRecordComment(params))
        : await dispatch(createRecordComment(params));
    if (!action.error) {
      onReloadGaps();
      hide();
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const onDecline = () => {
    hide();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return intl.formatDate(date, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const createHumanizedString = (absences, hospitalizations) => {
    let resultString = "";

    if (absences && absences.length > 0) {
      const absencesText = absences
        .map((absence) => {
          if (absence.effective_to) {
            return intl.formatMessage(
              {
                id: "app.gaps.absence_period",
                defaultMessage: "Absence: {from} to {to}",
              },
              {
                from: formatDate(absence.effective_from),
                to: formatDate(absence.effective_to),
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: "app.gaps.absence_from", // New or shared message ID for absences with only a start date
                defaultMessage: "Absence: from {from}",
              },
              {
                from: formatDate(absence.effective_from),
              }
            );
          }
        })
        .join("; ");
      resultString += absencesText;
    }

    if (hospitalizations && hospitalizations.length > 0) {
      if (resultString) resultString += ". ";

      const hospitalizationsText = hospitalizations
        .map((hospitalization) => {
          if (hospitalization.effective_to) {
            return intl.formatMessage(
              {
                id: "app.gaps.hospitalization_period",
                defaultMessage: "Hospitalization at {hospital}: {from} to {to}",
              },
              {
                hospital: hospitalization.hospitalization_institute?.name || "",
                from: formatDate(hospitalization.effective_from),
                to: formatDate(hospitalization.effective_to),
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: "app.gaps.hospitalization_from", // New message ID for hospitalizations with only a start date
                defaultMessage: "Hospitalization at {hospital}: from {from}",
              },
              {
                hospital: hospitalization.hospitalization_institute?.name || "",
                from: formatDate(hospitalization.effective_from),
              }
            );
          }
        })
        .join("; ");
      resultString += hospitalizationsText;
    }

    return resultString;
  };

  const createHumanizedStringForEmployeeAbsences = (employeeAbsences) => {
    let resultString = "";
    if (employeeAbsences && employeeAbsences.length > 0) {
      const absencesText = employeeAbsences
        .map((absence) => {
          if (absence.effective_to) {
            return intl.formatMessage(
              {
                id: "app.gaps.employee_absence_period",
                defaultMessage:
                  "Foreign worker absence: {from} to {to}{reason}",
              },
              {
                from: formatDate(absence.effective_from),
                to: formatDate(absence.effective_to),
                reason: absence.reason?.name ? ` (${absence.reason.name})` : "",
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: "app.gaps.employee_absence_from", // New message ID for absences with only a start date
                defaultMessage: "Foreign worker absence: from {from}{reason}",
              },
              {
                from: formatDate(absence.effective_from),
                reason: absence.reason?.name ? ` (${absence.reason.name})` : "",
              }
            );
          }
        })
        .join("; ");
      resultString += absencesText;
    }

    return resultString;
  };

  const filterByMonthAndYear = (data, month, year) => {
    if (!data || !data.length) return [];

    return data.filter((item) => {
      // Check if item overlaps with the target month/year
      const fromDate = new Date(item.effective_from);
      // Create date range for the target month/year
      const startOfMonth = new Date(year, month - 1, 1); // months are 0-indexed in JS Date
      const endOfMonth = new Date(year, month, 0); // Last day of the month

      if (item.effective_to == null) {
        // Handles both null and undefined for effective_to
        // If no end date, include if it starts anytime up to or on the end of the target month
        return fromDate <= endOfMonth;
      } else {
        // If there is an end date, use standard overlap logic
        const toDate = new Date(item.effective_to);
        // Check if there's any overlap between the item's date range and the target month/year
        return !(toDate < startOfMonth || fromDate > endOfMonth);
      }
    });
  };

  const onEntityValuesChange = async (all, form, values) => {
    // If managed_gap_reason_id changed, explicitly clear validation errors on the comment field
    if ("managed_gap_reason_id" in values) {
      // Clear validation errors for comment field if a value is selected
      if (values.managed_gap_reason_id) {
        form.setFields([
          {
            name: "comment",
            errors: [], // Clear the errors
          },
        ]);
      }
    }

    const patientId = record?.patient_id;
    const month = record?.month;
    const year = record?.year;
    if (values.managed_gap_reason_id === 1) {
      try {
        const absencesResponse = await getPatientAbsences({
          patientId,
          scopePrefix,
        });
        const hospitalizationsResponse = await getPatientHospitalizations({
          patientId,
          scopePrefix,
        });

        const allAbsences = absencesResponse?.data?.items || [];
        const allHospitalizations = hospitalizationsResponse?.data?.items || [];

        // Filter data to only include entries relevant to the specified month and year
        const absences = filterByMonthAndYear(allAbsences, month, year);
        const hospitalizations = filterByMonthAndYear(
          allHospitalizations,
          month,
          year
        );

        const humanizedString = createHumanizedString(
          absences,
          hospitalizations
        );

        // Set the value of the comment field
        if (humanizedString) {
          form.setFieldsValue({
            comment: humanizedString,
          });
        }
      } catch (error) {
        console.error("Error fetching patient data:", error);
      }
    } else if (values.managed_gap_reason_id === 2) {
      try {
        const isAdmin = location.pathname.includes("admin");
        const params = {
          id: patientId,
          branchId: branchId,
          admin: isAdmin,
        };

        const patientResponse = await getPatient(params);
        const foreign_worker_associations =
          patientResponse?.data?.foreign_worker_associations || [];

        if (foreign_worker_associations.length > 0) {
          const employeeId = foreign_worker_associations[0].employee_id;

          if (employeeId) {
            const absencesResponse = await getEmployeeAbsences({
              employeeId,
              scopePrefix,
            });

            const allEmployeeAbsences = absencesResponse?.data?.items || [];

            // Filter employee absences by month and year
            const employeeAbsences = filterByMonthAndYear(
              allEmployeeAbsences,
              month,
              year
            );
            // Create humanized string for employee absences
            const humanizedString =
              createHumanizedStringForEmployeeAbsences(employeeAbsences);

            // Set the value of the comment field
            if (humanizedString) {
              form.setFieldsValue({
                comment: humanizedString,
              });
            } else {
              // If no absences found for this period
              form.setFieldsValue({
                comment: "",
              });
            }
          }
        } else {
          // No foreign worker associated with this patient
          form.setFieldsValue({
            comment: "",
          });
        }
      } catch (error) {
        console.error("Error fetching foreign worker data:", error);
        // Show error in comment
        form.setFieldsValue({
          comment: "",
        });
      }
    } else if (
      values.managed_gap_reason_id === undefined &&
      "managed_gap_reason_id" in values &&
      all.comment !== "" &&
      all.comment !== undefined
    ) {
      form.setFieldsValue({
        comment: "",
      });
    }
  };

  // Function to create dynamic items based on form values
  const getDynamicItems = (formValues, form) => {
    const isCommentRequired = !formValues?.managed_gap_reason_id;
    return [
      ...(hasManagedGapReasonsFeature
        ? [
            {
              name: "managed_gap_reason_id",
              required: false,
              comp: (
                <SettingsDataSelect
                  name="managed_gap_reason_id"
                  category="gaps"
                  settingsdataName={"system_tables/managed_gap_reasons"}
                  required={false}
                  type="list"
                />
              ),
            },
          ]
        : []),
      {
        name: "comment",
        required: isCommentRequired,
        comp: (
          <TextAreaItem
            name="comment"
            category="gaps"
            required={isCommentRequired}
            autoFocus={true}
            rows={4}
            // Add a key to force re-render when the required status changes
            key={`comment-${isCommentRequired ? "required" : "optional"}`}
          />
        ),
      },
    ];
  };

  return (
    <Modal
      closable={false}
      maskClosable={false}
      open={record}
      footer={null}
      onCancel={hide}
      title={null}
      width={"fit-content"}
      styles={{
        body: {
          padding: 0,
        },
      }}
      destroyOnClose={true}
    >
      <AddEntityForm
        formLayout={{
          labelCol: { span: 7 },
          wrapperCol: { span: 17 },
        }}
        resource={"gaps"}
        action={"editor"}
        activeEntity={{
          ...record,
          comment: record?.order_gap_reason_comment,
          managed_gap_reason_id: record?.order_gap_reason_managed_reason_id,
        }}
        getDynamicItems={getDynamicItems} // Only passing the function now
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onDecline={onDecline}
        onEntityValuesChange={onEntityValuesChange}
        saveLoading={createLoading || updateLoading}
        addText={intl.formatMessage({
          id:
            type === "edit"
              ? "app.gaps.edit_comment_title"
              : "app.gaps.add_comment_title",
        })}
        mode={type}
      />
    </Modal>
  );
};

export default EditCommentModal;
